// Environment Configuration for OMAPP
// This file manages different API endpoints for different environments

const ENVIRONMENTS = {
  LOCAL: 'LOCAL',
  DEV: 'DEV', 
  PROD: 'PROD'
};

// Default configuration fallbacks
const DEFAULT_CONFIG = {
  [ENVIRONMENTS.LOCAL]: {
    BASE_URL: 'http://localhost:48055',
    API_BASE_URL: 'http://localhost:48055/ownersmanual',
    OAUTH_TOKEN_URL: 'http://localhost:48055/ownersmanual/oauth/token',
    API_URL: 'http://localhost:48055/ownersmanual/api/v1'
  },
  [ENVIRONMENTS.DEV]: {
    BASE_URL: 'https://ownermanualdev.bosch-automotive.com',
    API_BASE_URL: 'https://ownermanualdev.bosch-automotive.com/ownersmanual',
    OAUTH_TOKEN_URL: 'https://ownermanualdev.bosch-automotive.com/ownersmanual/oauth/token',
    API_URL: 'https://ownermanualdev.bosch-automotive.com/ownersmanual/api/v1'
  },
  [ENVIRONMENTS.PROD]: {
    BASE_URL: 'https://ownermanual.bosch-automotive.com',
    API_BASE_URL: 'https://ownermanual.bosch-automotive.com/ownersmanual',
    OAUTH_TOKEN_URL: 'https://ownermanual.bosch-automotive.com/ownersmanual/oauth/token',
    API_URL: 'https://ownermanual.bosch-automotive.com/ownersmanual/api/v1'
  }
};

// Valid environment values
const VALID_ENVIRONMENTS = [ENVIRONMENTS.LOCAL, ENVIRONMENTS.DEV, ENVIRONMENTS.PROD];

// Get current environment
const getCurrentEnvironment = () => {
  let detectedEnv = null;
  
  // ALWAYS prioritize REACT_APP_ENV - this should never be overridden
  if (process.env.REACT_APP_ENV) {
    const envValue = process.env.REACT_APP_ENV.toUpperCase().trim();
    detectedEnv = envValue;
  }
  
  // Check NODE_ENV only if REACT_APP_ENV is not set
  else if (process.env.NODE_ENV === 'production') {
    detectedEnv = ENVIRONMENTS.PROD;
  } else if (process.env.NODE_ENV === 'development') {
    detectedEnv = ENVIRONMENTS.DEV;
  }
  
  // Only use hostname auto-detection if NO environment variables are set
  else if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      detectedEnv = ENVIRONMENTS.LOCAL;
    } else if (hostname.includes('dev')) {
      detectedEnv = ENVIRONMENTS.DEV;
    } else if (hostname.includes('prod') || hostname.includes('bosch-automotive.com')) {
      detectedEnv = ENVIRONMENTS.PROD;
    }
  }
  
  // Validate the detected environment
  if (!detectedEnv || !VALID_ENVIRONMENTS.includes(detectedEnv)) {
    console.warn(`� Invalid environment detected: '${detectedEnv}', falling back to DEV`);
    detectedEnv = ENVIRONMENTS.DEV;
  }
  
  return detectedEnv;
};

// Get current configuration based on environment
const getConfig = () => {
  const currentEnv = getCurrentEnvironment();
  
  // ALWAYS use DEFAULT_CONFIG when REACT_APP_ENV is explicitly set
  // This ensures environment switching works correctly
  if (process.env.REACT_APP_ENV) {
    const explicitEnv = process.env.REACT_APP_ENV.toUpperCase().trim();
    if (DEFAULT_CONFIG[explicitEnv]) {
      return {
        ...DEFAULT_CONFIG[explicitEnv],
        ENVIRONMENT: explicitEnv
      };
    }
  }
  
  // Only use environment variables if no explicit REACT_APP_ENV is set
  const config = {
    BASE_URL: process.env.REACT_APP_BASE_URL || (DEFAULT_CONFIG[currentEnv] && DEFAULT_CONFIG[currentEnv].BASE_URL),
    API_BASE_URL: process.env.REACT_APP_API_BASE_URL || (DEFAULT_CONFIG[currentEnv] && DEFAULT_CONFIG[currentEnv].API_BASE_URL),
    OAUTH_TOKEN_URL: process.env.REACT_APP_OAUTH_TOKEN_URL || (DEFAULT_CONFIG[currentEnv] && DEFAULT_CONFIG[currentEnv].OAUTH_TOKEN_URL),
    API_URL: process.env.REACT_APP_API_URL || (DEFAULT_CONFIG[currentEnv] && DEFAULT_CONFIG[currentEnv].API_URL),
    ENVIRONMENT: currentEnv
  };
  
  if (!config.BASE_URL) {
    console.warn(`Environment '${currentEnv}' not found, falling back to DEV`);
    return {
      ...DEFAULT_CONFIG[ENVIRONMENTS.DEV],
      ENVIRONMENT: ENVIRONMENTS.DEV
    };
  }
  
  return config;
};

// Export the configuration
const AppConfig = getConfig();

// Helper functions to build URLs
export const buildApiUrl = (endpoint) => {
  // Add safety check for undefined/null endpoint
  if (!endpoint || typeof endpoint !== 'string') {
    console.warn('⚠️ buildApiUrl called with invalid endpoint:', endpoint);
    return AppConfig.API_URL;
  }
  
  return `${AppConfig.API_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
};

export const buildOAuthUrl = () => {
  const url = AppConfig.OAUTH_TOKEN_URL;
  return url;
};

export const buildUploadUrl = (languageUid, vehicleModelUid, publicationTypeUid) => {
  // Add safety checks for undefined parameters
  const safeLanguageUid = languageUid || '';
  const safeVehicleModelUid = vehicleModelUid || '';
  const safePublicationTypeUid = publicationTypeUid || '';
  
  return `${AppConfig.API_URL}/vehicle/manual/upload?languageUid=${safeLanguageUid}&vehicleModelUid=${safeVehicleModelUid}&publicationTypeUid=${safePublicationTypeUid}`;
};

export { ENVIRONMENTS, AppConfig };
export default AppConfig;
